import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CalendarIcon, DocumentTextIcon, CreditCardIcon, UserIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  getMyApplications,
  cancelApplication as cancelApplicationAPI,
  getMyChildren,
  Child as SupabaseChild
} from '../services/applicationService';
import { Application } from '../types';

// Extended Child interface for local UI state
interface Child extends SupabaseChild {
  school?: string;
  grade?: string;
  relationship?: string;
}

const MyPage: React.FC = () => {
  const { user, isStudent, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'applications'>('applications');

  // 신청 내역 상태
  const [applications, setApplications] = useState<Application[]>([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'registered' | 'waiting' | 'cancelled' | 'all'>('all');
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // 회원정보 수정 상태
  const [editMode, setEditMode] = useState(false);
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [saving, setSaving] = useState(false);

  // 자녀 관리 상태
  const [children, setChildren] = useState<Child[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [showChildModal, setShowChildModal] = useState(false);
  const [editingChild, setEditingChild] = useState<Child | null>(null);
  const [childForm, setChildForm] = useState({
    name: '',
    birthdate: '',
    gender: 'male' as 'male' | 'female',
    school: '',
    grade: '',
    relationship: '자녀',
  });

  // user가 변경될 때마다 폼 초기화
  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setAddress(user.address || '');
      setAddressDetail(user.address_detail || '');
    }
  }, [user]);

  // 신청 내역 로드
  useEffect(() => {
    if (user && activeTab === 'applications') {
      loadApplications();
    }
  }, [user, activeTab, statusFilter]);

  // 자녀 목록 로드 (보호자만)
  useEffect(() => {
    if (user?.user_type === 'guardian') {
      loadChildren();
    }
  }, [user]);

  const loadApplications = async () => {
    setLoadingApplications(true);
    try {
      const response = await getMyApplications(statusFilter);
      console.log('=== MyPage: loadApplications 응답 ===');
      console.log('response:', response);

      if (response.success && response.data) {
        console.log('applications 데이터:', response.data.applications);
        if (response.data.applications.length > 0) {
          console.log('첫 번째 신청의 class_name:', response.data.applications[0].class_name);
        }
        setApplications(response.data.applications);
      } else {
        console.error('Failed to load applications:', response.error);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoadingApplications(false);
    }
  };

  const loadChildren = async () => {
    setLoadingChildren(true);
    try {
      const childrenData = await getMyChildren();
      setChildren(childrenData);
    } catch (error) {
      console.error('Error loading children:', error);
    } finally {
      setLoadingChildren(false);
    }
  };

  if (!user || !isStudent) {
    return (
      <div>
        <Header />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="text-center py-12">
            <p className="text-gray-500">로그인해주세요.</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'registered': return '접수완료';
      case 'waiting': return '대기중';
      case 'cancelled': return '신청취소';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'registered': return 'bg-green-100 text-green-800';
      case 'waiting': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusLabel = (paymentStatus: string | null | undefined) => {
    switch (paymentStatus) {
      case 'pending': return '결제대기';
      case 'completed': return '결제완료';
      case 'expired': return '기한만료';
      case 'cancelled': return '결제취소';
      case 'refunded': return '환불완료';
      default: return null;
    }
  };

  const getPaymentStatusColor = (paymentStatus: string | null | undefined) => {
    switch (paymentStatus) {
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      case 'refunded': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isPaymentDeadlineExpired = (deadline: string | null | undefined) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  const handleCancelApplication = async (applicationId: string) => {
    if (!window.confirm('수강신청을 취소하시겠습니까?\n\n취소 후 다시 신청하시려면 대기 순번이 변경될 수 있습니다.')) {
      return;
    }

    setCancellingId(applicationId);

    try {
      const response = await cancelApplicationAPI({ application_id: applicationId });

      if (response.success) {
        // 승급 정보 확인
        if (response.data?.promoted) {
          alert(`✅ 수강신청이 취소되었습니다.\n\n🎉 대기자 ${response.data.promoted.student_name}님이 자동으로 등록되었습니다.`);
        } else {
          alert('✅ 수강신청이 취소되었습니다.');
        }
        // 목록 새로고침
        await loadApplications();
      } else if (response.error) {
        const { code, message } = response.error;
        switch (code) {
          case 'APPLICATION_NOT_FOUND':
            alert('신청 내역을 찾을 수 없습니다.');
            break;
          case 'ALREADY_CANCELLED':
            alert('이미 취소된 신청입니다.');
            break;
          case 'UNAUTHORIZED':
            alert('본인의 신청만 취소할 수 있습니다.');
            break;
          default:
            alert(message || '취소 처리 중 오류가 발생했습니다.\n잠시 후 다시 시도해주세요.');
        }
        await loadApplications();
      }
    } catch (error) {
      console.error('Cancel application error:', error);
      alert('네트워크 오류가 발생했습니다.\n\n인터넷 연결을 확인하고 다시 시도해주세요.');
    } finally {
      setCancellingId(null);
    }
  };

  // 회원정보 저장
  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveMessage('');

    const result = await updateProfile({
      email,
      address,
      address_detail: addressDetail,
    });

    if (result.success) {
      setSaveMessage(result.message || '저장되었습니다.');
      setEditMode(false);
      // 3초 후 메시지 제거
      setTimeout(() => setSaveMessage(''), 3000);
    } else {
      setSaveMessage(result.message || '저장에 실패했습니다.');
    }

    setSaving(false);
  };

  // 수정 취소
  const handleCancelEdit = () => {
    if (user) {
      setEmail(user.email || '');
      setAddress(user.address || '');
      setAddressDetail(user.address_detail || '');
    }
    setEditMode(false);
    setSaveMessage('');
  };

  // 자녀 추가 모달 열기
  const handleOpenAddChild = () => {
    setEditingChild(null);
    setChildForm({
      name: '',
      birthdate: '',
      gender: 'male',
      school: '',
      grade: '',
      relationship: '자녀',
    });
    setShowChildModal(true);
  };

  // 자녀 수정 모달 열기
  const handleEditChild = (child: Child) => {
    setEditingChild(child);
    setChildForm({
      name: child.name,
      birthdate: child.birthdate || '',
      gender: child.gender || 'male',
      school: child.school || '',
      grade: child.grade || '',
      relationship: child.relationship || '자녀',
    });
    setShowChildModal(true);
  };

  // 자녀 저장
  const handleSaveChild = () => {
    if (!childForm.name || !childForm.birthdate) {
      alert('이름과 생년월일은 필수입니다.');
      return;
    }

    if (editingChild) {
      // 수정
      setChildren(children.map(c =>
        c.id === editingChild.id
          ? { ...editingChild, ...childForm }
          : c
      ));
    } else {
      // 추가
      const newChild: Child = {
        id: Date.now().toString(),
        name: childForm.name,
        birthdate: childForm.birthdate,
        gender: childForm.gender,
        guardian_id: user?.id || '',
        created_at: new Date().toISOString(),
        school: childForm.school,
        grade: childForm.grade,
        relationship: childForm.relationship,
      };
      setChildren([...children, newChild]);
    }

    setShowChildModal(false);
    setSaveMessage('자녀 정보가 저장되었습니다.');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  // 자녀 삭제
  const handleDeleteChild = (childId: string) => {
    if (window.confirm('자녀 정보를 삭제하시겠습니까?')) {
      setChildren(children.filter(c => c.id !== childId));
      setSaveMessage('자녀 정보가 삭제되었습니다.');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 bg-gray-50 py-12 mt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">마이페이지</h1>
            <p className="text-gray-600">{user.name}님의 수강신청 내역을 확인하세요.</p>
          </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('applications')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'applications'
                    ? 'border-[#48c9a6] text-[#48c9a6]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                수강신청 내역
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-[#48c9a6] text-[#48c9a6]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                회원 정보
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'applications' && (
          <div>
            {/* 상태 필터 */}
            <div className="mb-4 flex gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-[#48c9a6] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                전체
              </button>
              <button
                onClick={() => setStatusFilter('registered')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'registered'
                    ? 'bg-[#48c9a6] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                접수완료
              </button>
              <button
                onClick={() => setStatusFilter('waiting')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'waiting'
                    ? 'bg-[#48c9a6] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                대기중
              </button>
              <button
                onClick={() => setStatusFilter('cancelled')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'cancelled'
                    ? 'bg-[#48c9a6] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                신청취소
              </button>
            </div>

            <div className="bg-white rounded-lg shadow">
              {loadingApplications ? (
                <div className="text-center py-12">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#48c9a6] border-r-transparent"></div>
                  <p className="mt-4 text-gray-500">불러오는 중...</p>
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">신청 내역이 없습니다</h3>
                  <p className="mt-2 text-gray-500">
                    {statusFilter !== 'all'
                      ? `${getStatusLabel(statusFilter)} 상태의 신청 내역이 없습니다.`
                      : '아직 수강신청한 프로그램이 없습니다.'}
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="space-y-6">
                      {applications.map((application) => (
                        <div key={application.application_id} className="border border-gray-200 rounded-lg p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {application.program_name}
                                {application.class_name && application.class_name !== '기본반' && (
                                  <span className="ml-2 text-base text-gray-600">({application.class_name})</span>
                                )}
                              </h3>
                              {application.instructor && (
                                <p className="text-sm text-gray-600 mt-1">강사: {application.instructor}</p>
                              )}
                              <p className="text-sm text-gray-500 mt-1">수강생: {application.student_name}</p>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <div className="flex flex-wrap gap-2 justify-end">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(application.status)}`}>
                                  {getStatusLabel(application.status)}
                                </span>
                                {application.payment_status && application.status === 'registered' && (
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPaymentStatusColor(application.payment_status)}`}>
                                    {getPaymentStatusLabel(application.payment_status)}
                                  </span>
                                )}
                              </div>
                              {application.status === 'waiting' && application.waiting_number && (
                                <span className="text-xs text-gray-600 bg-yellow-50 px-2 py-1 rounded">
                                  대기순번: {application.waiting_number}번
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="flex items-center text-sm text-gray-600">
                              <CalendarIcon className="h-4 w-4 mr-2" />
                              신청일: {new Date(application.created_at).toLocaleDateString('ko-KR')}
                            </div>
                            {application.tuition !== undefined && application.material_fee !== undefined && (
                              <div className="flex items-center text-sm text-gray-600">
                                <CreditCardIcon className="h-4 w-4 mr-2" />
                                금액: {(application.tuition + application.material_fee).toLocaleString()}원
                              </div>
                            )}
                            {application.course_start_date && application.course_end_date && (
                              <div className="flex items-center text-sm text-gray-600">
                                <CalendarIcon className="h-4 w-4 mr-2" />
                                교육기간: {new Date(application.course_start_date).toLocaleDateString('ko-KR')} ~ {new Date(application.course_end_date).toLocaleDateString('ko-KR')}
                              </div>
                            )}
                            {application.course_start_time && application.course_end_time && (
                              <div className="flex items-center text-sm text-gray-600">
                                <DocumentTextIcon className="h-4 w-4 mr-2" />
                                교육시간: {application.course_start_time} ~ {application.course_end_time}
                              </div>
                            )}
                            {/* 결제 정보 (Phase 3) */}
                            {application.payment_amount && application.payment_amount > 0 && application.status === 'registered' && (
                              <div className="flex items-center text-sm text-gray-600">
                                <CreditCardIcon className="h-4 w-4 mr-2" />
                                결제금액: {application.payment_amount.toLocaleString()}원
                              </div>
                            )}
                            {application.payment_deadline && application.payment_status === 'pending' && (
                              <div className={`flex items-center text-sm ${isPaymentDeadlineExpired(application.payment_deadline) ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                                <CalendarIcon className="h-4 w-4 mr-2" />
                                결제기한: {new Date(application.payment_deadline).toLocaleDateString('ko-KR')}
                                {isPaymentDeadlineExpired(application.payment_deadline) && ' (만료)'}
                              </div>
                            )}
                            {application.paid_at && application.payment_status === 'completed' && (
                              <div className="flex items-center text-sm text-gray-600">
                                <CalendarIcon className="h-4 w-4 mr-2" />
                                결제일: {new Date(application.paid_at).toLocaleDateString('ko-KR')}
                              </div>
                            )}
                            {application.cancelled_at && (
                              <div className="flex items-center text-sm text-gray-600">
                                <CalendarIcon className="h-4 w-4 mr-2" />
                                취소일: {new Date(application.cancelled_at).toLocaleDateString('ko-KR')}
                              </div>
                            )}
                          </div>

                          {(application.status === 'registered' || application.status === 'waiting') && (
                            <div className="flex justify-end gap-3">
                              {/* 결제 관련 버튼 (Phase 3) */}
                              {application.status === 'registered' && application.payment_status === 'pending' && !isPaymentDeadlineExpired(application.payment_deadline) && (
                                <button
                                  onClick={() => {
                                    alert('결제 기능은 Phase 3에서 구현 예정입니다.\n\nPG SDK 연동 후 사용 가능합니다.');
                                    // TODO: navigate(`/payment/${application.application_id}`);
                                  }}
                                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                                >
                                  결제하기
                                </button>
                              )}
                              {application.status === 'registered' && application.payment_status === 'completed' && application.receipt_url && (
                                <button
                                  onClick={() => {
                                    if (application.receipt_url) {
                                      window.open(application.receipt_url, '_blank');
                                    } else {
                                      alert('영수증 정보가 없습니다.');
                                    }
                                  }}
                                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                                >
                                  영수증 보기
                                </button>
                              )}

                              {/* 신청 취소 버튼 */}
                              <button
                                onClick={() => {
                                  // 결제 완료된 경우 취소 불가
                                  if (application.payment_status === 'completed') {
                                    alert('결제가 완료된 신청은 취소할 수 없습니다.\n\n환불을 원하시면 고객센터로 문의해주세요.');
                                    return;
                                  }
                                  handleCancelApplication(application.application_id);
                                }}
                                disabled={cancellingId === application.application_id}
                                className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {cancellingId === application.application_id ? '취소 처리 중...' : '신청 취소'}
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div>
            <div className="bg-white rounded-lg shadow">
              <div className="px-4 py-5 sm:p-6">
                {/* 저장 성공/실패 메시지 */}
                {saveMessage && (
                  <div className={`mb-6 p-4 rounded-lg ${saveMessage.includes('실패') ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    <p className={`text-sm ${saveMessage.includes('실패') ? 'text-red-800' : 'text-green-800'}`}>
                      {saveMessage}
                    </p>
                  </div>
                )}

                {/* 본인인증 정보 (읽기 전용) */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">본인인증 정보</h3>
                    <span className="text-xs text-gray-500 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      변경 불가
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                      <p className="text-gray-900">{user.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">생년월일</label>
                      <p className="text-gray-900">{user.birthdate || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">성별</label>
                      <p className="text-gray-900">
                        {user.gender === 'male' ? '남성' : user.gender === 'female' ? '여성' : '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">휴대폰 번호</label>
                      <p className="text-gray-900">{user.phone || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* 수정 가능한 정보 */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">회원 정보</h3>
                    {!editMode && (
                      <button
                        onClick={() => setEditMode(true)}
                        className="px-4 py-2 text-sm font-medium text-white bg-[#48c9a6] hover:bg-[#3ab89a] rounded-lg transition-colors"
                      >
                        정보 수정
                      </button>
                    )}
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        이메일 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={!editMode}
                        className={`block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm ${
                          editMode
                            ? 'focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]'
                            : 'bg-gray-50 cursor-not-allowed'
                        }`}
                      />
                    </div>

                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                        주소
                      </label>
                      <input
                        type="text"
                        id="address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        disabled={!editMode}
                        placeholder="예: 경기도 동두천시"
                        className={`block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm ${
                          editMode
                            ? 'focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]'
                            : 'bg-gray-50 cursor-not-allowed'
                        }`}
                      />
                    </div>

                    <div>
                      <label htmlFor="addressDetail" className="block text-sm font-medium text-gray-700 mb-1">
                        상세주소
                      </label>
                      <input
                        type="text"
                        id="addressDetail"
                        value={addressDetail}
                        onChange={(e) => setAddressDetail(e.target.value)}
                        disabled={!editMode}
                        placeholder="동/호수 등"
                        className={`block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm ${
                          editMode
                            ? 'focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]'
                            : 'bg-gray-50 cursor-not-allowed'
                        }`}
                      />
                    </div>

                    {editMode && (
                      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                          onClick={handleCancelEdit}
                          disabled={saving}
                          className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                          취소
                        </button>
                        <button
                          onClick={handleSaveProfile}
                          disabled={saving}
                          className="px-6 py-2 text-sm font-medium text-white bg-[#48c9a6] hover:bg-[#3ab89a] rounded-lg transition-colors disabled:opacity-50"
                        >
                          {saving ? '저장 중...' : '저장'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* 자녀 관리 (보호자만) */}
                {user.user_type === 'guardian' && (
                  <div className="mb-8 pt-8 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">자녀 관리</h3>
                      <button
                        onClick={handleOpenAddChild}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#48c9a6] hover:bg-[#3ab89a] rounded-lg transition-colors"
                      >
                        <PlusIcon className="w-4 h-4" />
                        자녀 추가
                      </button>
                    </div>

                    {children.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-4 text-lg font-medium text-gray-900">등록된 자녀가 없습니다</h3>
                        <p className="mt-2 text-gray-500">자녀 추가 버튼을 눌러 자녀 정보를 등록하세요.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {children.map((child) => (
                          <div key={child.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <h4 className="text-lg font-semibold text-gray-900">{child.name}</h4>
                                <p className="text-sm text-gray-500">{child.relationship}</p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditChild(child)}
                                  className="p-1 text-gray-600 hover:text-[#48c9a6] transition-colors"
                                  title="수정"
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteChild(child.id)}
                                  className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                                  title="삭제"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                            <div className="space-y-1 text-sm text-gray-600">
                              <p>생년월일: {child.birthdate || '-'}</p>
                              <p>성별: {child.gender === 'male' ? '남' : child.gender === 'female' ? '여' : '-'}</p>
                              {child.school && <p>학교: {child.school}</p>}
                              {child.grade && <p>학년: {child.grade}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 자녀 추가/수정 모달 */}
        {showChildModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {editingChild ? '자녀 정보 수정' : '자녀 추가'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={childForm.name}
                    onChange={(e) => setChildForm({ ...childForm, name: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    생년월일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={childForm.birthdate}
                    onChange={(e) => setChildForm({ ...childForm, birthdate: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    성별 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="male"
                        checked={childForm.gender === 'male'}
                        onChange={(e) => setChildForm({ ...childForm, gender: 'male' })}
                        className="w-4 h-4 text-[#48c9a6] focus:ring-[#48c9a6]"
                      />
                      <span className="ml-2 text-gray-700">남</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="female"
                        checked={childForm.gender === 'female'}
                        onChange={(e) => setChildForm({ ...childForm, gender: 'female' })}
                        className="w-4 h-4 text-[#48c9a6] focus:ring-[#48c9a6]"
                      />
                      <span className="ml-2 text-gray-700">여</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">학교</label>
                  <input
                    type="text"
                    value={childForm.school}
                    onChange={(e) => setChildForm({ ...childForm, school: e.target.value })}
                    placeholder="예: 동두천초등학교"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">학년</label>
                  <input
                    type="text"
                    value={childForm.grade}
                    onChange={(e) => setChildForm({ ...childForm, grade: e.target.value })}
                    placeholder="예: 3학년"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">관계</label>
                  <select
                    value={childForm.relationship}
                    onChange={(e) => setChildForm({ ...childForm, relationship: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                  >
                    <option value="자녀">자녀</option>
                    <option value="손자/손녀">손자/손녀</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowChildModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={handleSaveChild}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#48c9a6] hover:bg-[#3ab89a] rounded-lg"
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MyPage;